import React, { useState, useEffect } from 'react';

// Mock form data for demo
const MOCK_FORM = {
  id: "demo-form",
  title: "Best form",
  description: "This is a demo form showcasing the phone field with country selection",
  settings: {
    theme_color: "#11111aff",
    section_button_color: "#11111aff",
    thank_you_message: "Thank you for your response!",
    thank_you_text_description: "Your response has been recorded."
  },
  fields: {
    steps: [
      {
        title: "Step 1",
        fields: [
          {
            id: "name",
            type: "text",
            label: "Your name",
            placeholder: "Enter your name",
            required: true
          },
          {
            id: "phone",
            type: "phone",
            label: "Phone number",
            placeholder: "Enter phone number",
            required: true,
            requireVerification: false // Set to true to test verification flow
          },
          {
            id: "birthdate",
            type: "date",
            label: "Birthdate",
            placeholder: "mm/dd/yyyy",
            required: false
          }
        ]
      }
    ]
  }
};

// Common country codes with flag emojis
const COUNTRY_CODES = [
  { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '+1', country: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+45', country: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: '+46', country: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: '+31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+41', country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+351', country: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: '+32', country: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: '+43', country: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: '+48', country: 'PL', name: 'Poland', flag: '🇵🇱' },
];

// Function to detect user's country based on IP (mock for demo)
async function detectUserCountry() {
  // Mock country detection - you can change this to test different countries
  return COUNTRY_CODES.find(country => country.country === 'DK') || COUNTRY_CODES[9];
}

export default function DemoPublicForm({ embed }) {
  const [form, setForm] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [phoneVerification, setPhoneVerification] = useState({});
  
  const steps = form?.fields?.steps || [];
  const currStep = steps[stepIdx];

  const defaultTheme = "#11111aff";
  const themeColor = form?.settings?.theme_color || defaultTheme;
  const buttonColor = form?.settings?.section_button_color || defaultTheme;

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setForm(MOCK_FORM);
      setLoading(false);
    }, 500);
  }, []);

  // Validation on step
  const validateStep = () => {
    let stepErrors = {};
    (currStep.fields || []).forEach(field => {
      if (field.required && !responses[field.id]?.toString().trim()) {
        stepErrors[field.id] = 'Required';
      }
    });
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validateStep()) setStepIdx(stepIdx + 1);
  };

  const handleBack = (e) => {
    e.preventDefault();
    const hasVerifiedPhone = Object.values(phoneVerification).some(verification => verification.verified);
    if (hasVerifiedPhone) return;
    
    setStepIdx(stepIdx - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    
    // Mock submission
    console.log('Demo form submitted with data:', responses);
    setSubmitted(true);
  };

  const handleChange = (field, value) => {
    setResponses(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  if (loading)
    return <div className="p-10 text-center text-lg">Loading demo form...</div>;
  if (!form)
    return <div className="p-10 text-center text-lg text-red-500">Demo form not found</div>;
  if (!currStep)
    return <div className="p-10 text-center text-lg text-red-500">No steps available</div>;
  if (submitted) {
    return <div className="p-12 text-center">
      <h2 className="text-3xl font-bold text-green-600 mb-2">{form.settings?.thank_you_message || "Thank you for your response!"}</h2>
      <div className="text-gray-600">{form.settings?.thank_you_text_description || "Your response has been recorded."}</div>
      <div className="mt-6 p-4 bg-gray-100 rounded-lg">
        <h3 className="font-bold mb-2">Demo Data Submitted:</h3>
        <pre className="text-left text-sm">{JSON.stringify(responses, null, 2)}</pre>
      </div>
    </div>;
  }

  return (
    <div className="flex min-h-screen flex-col items-center ">
      <div className="w-full mx-auto p-5" style={{ maxWidth: embed ? '100%' : '50%' }}>
        {!embed && (
          <>
            <h1 className="text-4xl font-extrabold text-center mt-10 mb-2">{form.title}</h1>
            {form.description && (
              <div className="text-center text-gray-500 mb-8">{form.description}</div>
            )}
          </>
        )}
        {(steps.length > 1 && !embed) && (
          <ProgressBar stepIdx={stepIdx} steps={steps.length} color={themeColor} />
        )}
        <form
          className="bg-white p-8 rounded-xl shadow-lg transition-all mt-6"
          autoComplete="off"
          onSubmit={stepIdx === steps.length - 1 ? handleSubmit : handleNext}
        >
          <div className="mb-8">
            <div className="text-xl font-bold mb-3">{currStep?.title ? ` ${currStep.title}` : ''}</div>
            {(currStep?.fields || []).map(field => (
              <FormField
                key={field.id}
                field={field}
                value={responses[field.id] || (field.type === 'checkbox' ? [] : '')}
                onChange={val => handleChange(field.id, val)}
                error={errors[field.id]}
                phoneVerification={phoneVerification}
                setPhoneVerification={setPhoneVerification}
              />
            ))}
          </div>
          <div className="flex space-x-3 max-w-md">
            {stepIdx > 0 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={Object.values(phoneVerification).some(verification => verification.verified)}
                className={`flex-1 rounded-sm border px-4 py-2 font-medium shadow transition-colors ${
                  Object.values(phoneVerification).some(verification => verification.verified)
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >Back</button>
            )}
            <button
              type="submit"
              className="flex-1 rounded-sm text-white font-bold px-4 py-2 shadow-md hover:bg-indigo-700 transition-colors"
              style={{ backgroundColor: buttonColor }}
            >
              {stepIdx === steps.length - 1 ? 'Submit' : 'Next'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProgressBar({ stepIdx, steps, color }) {
  return (
    <div className="w-full h-3 rounded-full mb-8 overflow-hidden">
      <div
        className="h-full transition-all"
        style={{
          width: `${((stepIdx + 1) / steps) * 100}%`,
          background: color
        }}
      />
    </div>
  );
}

// Move PhoneField outside of component to prevent recreation
const PhoneField = React.memo(function PhoneField({ field, value, onChange, error, phoneVerification, setPhoneVerification }) {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[9]); // Default to Denmark
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countryDetected, setCountryDetected] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const buttonRef = React.useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = React.useRef(null);
  
  // Auto-detect country on component mount
  useEffect(() => {
    if (!countryDetected) {
      detectUserCountry().then(detectedCountry => {
        setSelectedCountry(detectedCountry);
        setCountryDetected(true);
      });
    }
  }, [countryDetected]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showCountryDropdown && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showCountryDropdown]);

  // Filter countries based on search query
  const filteredCountries = COUNTRY_CODES.filter(country => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      country.name.toLowerCase().includes(query) ||
      country.country.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  });
  
  const fieldVerification = phoneVerification[field.id] || { 
    codeSent: false, 
    verified: false,
    error: null 
  };

  const updateVerificationState = (updates) => {
    setPhoneVerification(prev => ({
      ...prev,
      [field.id]: { ...fieldVerification, ...updates }
    }));
  };

  const sendVerificationCode = async () => {
    const fullPhoneNumber = selectedCountry.code + phoneNumber;
    setSendingCode(true);
    
    // Mock verification code sending
    setTimeout(() => {
      console.log('Demo: Sending verification code to', fullPhoneNumber);
      updateVerificationState({ codeSent: true, error: null });
      setSendingCode(false);
    }, 1000);
  };

  const verifyCode = async () => {
    setVerifyingCode(true);
    
    // Mock verification - accept any 4-digit code for demo
    setTimeout(() => {
      console.log('Demo: Verifying code', verificationCode);
      if (verificationCode === '1234') {
        updateVerificationState({ verified: true, error: null });
        onChange(selectedCountry.code + phoneNumber);
      } else {
        updateVerificationState({ error: 'Demo: Use code "1234" for verification' });
      }
      setVerifyingCode(false);
    }, 500);
  };

  const handlePhoneChange = (newPhoneNumber) => {
    setPhoneNumber(newPhoneNumber);
    if (!field.requireVerification) {
      onChange(selectedCountry.code + newPhoneNumber);
    } else {
      updateVerificationState({ codeSent: false, verified: false, error: null });
    }
  };

  return (
    <div className="mb-8">
      <label className="block text-lg font-medium mb-4 text-gray-900">
        {field.label} {field.required && <span className="text-red-500">*</span>}
        {field.requireVerification && (
          <span className="ml-2 text-sm text-blue-600 font-normal">(Verification required - use code "1234")</span>
        )}
      </label>
      
      {/* Phone Input Container - Hidden during verification */}
      {!fieldVerification.codeSent && !fieldVerification.verified && (
        <div className="relative">
          <div className="flex items-center bg-white border-2 border-gray-200 rounded-lg focus-within:border-blue-500 transition-colors overflow-hidden">
            {/* Country Selector */}
            <div className="relative flex-shrink-0" ref={buttonRef}>
              <button
                type="button"
                className="flex items-center space-x-2 px-4 py-4 hover:bg-gray-50 transition-colors focus:outline-none"
                onClick={() => {
                  if (!showCountryDropdown && buttonRef.current) {
                    const rect = buttonRef.current.getBoundingClientRect();
                    setButtonRect(rect);
                  }
                  setSearchQuery(''); // Clear search when opening
                  setShowCountryDropdown(!showCountryDropdown);
                }}
              >
                <span className="text-xl" style={{ fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif' }}>{selectedCountry.flag}</span>
                <span className="text-gray-700 font-medium">{selectedCountry.code}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Country Dropdown */}
              {showCountryDropdown && (
                <div 
                  className="fixed w-80 max-h-80 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                  style={{ 
                    zIndex: 9999,
                    top: buttonRect?.bottom + 5,
                    left: buttonRect?.left 
                  }}
                >
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search countries..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  {/* Countries List */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country, index) => (
                        <button
                          key={`${country.code}-${country.country}`}
                          type="button"
                          className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            setSelectedCountry(country);
                            setShowCountryDropdown(false);
                            setSearchQuery('');
                            if (!field.requireVerification && phoneNumber) {
                              onChange(country.code + phoneNumber);
                            }
                          }}
                        >
                          <span className="text-lg" style={{ fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif' }}>{country.flag}</span>
                          <span className="text-gray-700 font-medium min-w-12">{country.code}</span>
                          <span className="text-gray-600 text-sm">{country.name}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        No countries found matching "{searchQuery}"
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Overlay to close dropdown */}
              {showCountryDropdown && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowCountryDropdown(false)}
                ></div>
              )}
            </div>

            {/* Phone Number Input */}
            <input
              type="tel"
              className="flex-1 px-4 py-4 text-lg border-0 focus:outline-none focus:ring-0 bg-transparent placeholder-gray-400"
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={e => handlePhoneChange(e.target.value)}
              required={field.required}
            />
          </div>
        </div>
      )}

      {/* Verification Section */}
      {field.requireVerification && (
        <div className="mt-6">
          {!fieldVerification.codeSent ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Click to send a verification code to {selectedCountry.flag} {selectedCountry.code} {phoneNumber}
              </p>
              <button
                type="button"
                onClick={sendVerificationCode}
                disabled={!phoneNumber || sendingCode}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingCode ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Verification Code (Demo)'
                )}
              </button>
            </div>
          ) : !fieldVerification.verified ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Code sent to {selectedCountry.flag} {selectedCountry.code} {phoneNumber}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  maxLength="4"
                  pattern="[0-9]{4}"
                  className="w-24 px-4 py-3 text-center text-lg font-mono border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="0000"
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                />
                <button
                  type="button"
                  onClick={verifyCode}
                  disabled={verificationCode.length !== 4 || verifyingCode}
                  className="px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {verifyingCode ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={() => updateVerificationState({ codeSent: false, error: null })}
                  className="px-4 py-3 text-gray-600 text-sm hover:text-gray-800 transition-colors"
                >
                  Resend code
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Phone number verified successfully</span>
            </div>
          )}
          
          {fieldVerification.error && (
            <div className="mt-3 flex items-center space-x-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{fieldVerification.error}</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-red-500 text-sm mt-3 flex items-center space-x-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </p>
      )}
    </div>
  );
});

function FormField({ field, value, onChange, error, phoneVerification, setPhoneVerification }) {
  const inputClass = `w-full mt-3 px-0 py-2 border-0 border-b-2 border-b-black-500 focus:outline-none focus:ring-0 ${
    error ? 'border-red-400 ring-red-200' : 'border-gray-300 focus:ring-indigo-300'
  }`;

  switch (field.type) {
    case 'text':
    case 'email':
    case 'number':
    case 'date':
      return (
        <div className="mb-4">
          <label className="block font-semibold mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <input
            type={field.type}
            className={inputClass}
            placeholder={field.placeholder || ''}
            required={field.required}
            value={value}
            onChange={e => onChange(e.target.value)}
          />
          {error && (
            <p className="text-red-500 text-xs mt-1">{error}</p>
          )}
        </div>
      );
    case 'phone':
      return (
        <PhoneField 
          field={field} 
          value={value} 
          onChange={onChange} 
          error={error}
          phoneVerification={phoneVerification}
          setPhoneVerification={setPhoneVerification}
        />
      );
    case 'textarea':
      return (
        <div className="mb-4">
          <label className="block font-semibold mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            className={inputClass}
            placeholder={field.placeholder || ''}
            required={field.required}
            value={value}
            onChange={e => onChange(e.target.value)}
            rows={4}
          />
          {error && (
            <p className="text-red-500 text-xs mt-1">{error}</p>
          )}
        </div>
      );
    case 'radio':
      return (
        <div className="mb-4">
          <div className="font-semibold mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {(field.options || []).map(option => (
              <label className="mr-4 flex items-center" key={option}>
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={() => onChange(option)}
                  required={field.required}
                  className="mr-2"
                />
                {option}
              </label>
            ))}
          </div>
          {error && (
            <p className="text-red-500 text-xs mt-1">{error}</p>
          )}
        </div>
      );
    case 'select':
      return (
        <div className="mb-4">
          <label className="block font-semibold mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <select
            className={inputClass}
            required={field.required}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
          >
            <option value="">{field.placeholder || 'Select an option...'}</option>
            {(field.options || []).map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
          {error && (
            <p className="text-red-500 text-xs mt-1">{error}</p>
          )}
        </div>
      );
    case 'checkbox':
      return (
        <div className="mb-4">
          <div className="font-semibold mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {(field.options || []).map(option => (
              <label className="mr-4 flex items-center" key={option}>
                <input
                  type="checkbox"
                  value={option}
                  checked={Array.isArray(value) && value.includes(option)}
                  onChange={e => {
                    let next;
                    if (Array.isArray(value)) {
                      next = e.target.checked
                        ? [...value, option]
                        : value.filter(v => v !== option);
                    } else {
                      next = e.target.checked ? [option] : [];
                    }
                    onChange(next);
                  }}
                  className="mr-2"
                />
                {option}
              </label>
            ))}
          </div>
          {error && (
            <p className="text-red-500 text-xs mt-1">{error}</p>
          )}
        </div>
      );
    default:
      return null;
  }
}
